import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VisuallyHidden,
  useDisclosure,
} from "@chakra-ui/react";
import { FiInbox, FiPlus, FiRotateCcw, FiSearch } from "react-icons/fi";
import { useQuery } from "@tanstack/react-query";

import { axiosClient } from "../client/http-client";
import CreateApplicationModal from "./createApplicationModal";
import { RequestCreditApplicationViewModal } from "./RequestCredit/applicationViewModal";
import { RowActions, clickableRowStyles } from "./applicationRowActions";
import {
  DataTable,
  EmptyState,
  formatShortDate,
  getStatusMeta,
  Pagination,
  SearchField,
  SectionCard,
  StatusBadge,
  TableSkeleton,
} from "./ui";

interface CreditApplication {
  id: number;
  application_id: string;
  status: string;
  creation_date: string;
  last_update: string;
}

const ACTIONS_COLUMN = "Actions";
const COLUMNS = ["Application ID", "Status", "Creation Date", "Last Update", ACTIONS_COLUMN];
const ITEMS_PER_PAGE = 5;

/** Credit account applications: the list, its detail view and starting a new one. Shown on My Contracts. */
const CreditApplications: React.FC<{ folio?: string }> = ({ folio }) => {
  const createApplication = useDisclosure();
  const viewApplication = useDisclosure();
  const [selectedId, setSelectedId] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const searchRef = useRef<HTMLInputElement>(null);

  // Same query (and cache) as the dashboard; this section only uses the credit applications
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["applications"],
    queryFn: async () => (await axiosClient.get("/applications")).data,
  });
  const applications: CreditApplication[] = data?.requestcredit ?? [];

  // Matches the raw values (ISO dates, API status) and the forms the table displays (dd/mm/yyyy, badge label)
  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return applications;
    return applications.filter(
      (app) =>
        app.application_id.toLowerCase().includes(term) ||
        app.status.toLowerCase().includes(term) ||
        app.creation_date.toLowerCase().includes(term) ||
        app.last_update.toLowerCase().includes(term) ||
        formatShortDate(new Date(app.creation_date)).includes(term) ||
        formatShortDate(new Date(app.last_update)).includes(term) ||
        getStatusMeta("application", app.status).label.toLowerCase().includes(term)
    );
  }, [applications, searchTerm]);

  useEffect(() => setPage(1), [searchTerm]);

  // Clamp so a shrinking list (search or refetch) never leaves the table on an empty page
  const pageCount = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const currentItems = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const newApplicationButton = (onOpen: () => void, size?: "sm") => (
    <Button variant="primary" size={size} leftIcon={<FiPlus />} onClick={onOpen}>
      New credit application
    </Button>
  );

  const renderContent = () => {
    if (isPending) {
      return <TableSkeleton columns={COLUMNS} hiddenColumns={[ACTIONS_COLUMN]} rows={3} />;
    }

    if (isError && !data) {
      return (
        <Alert status="error" alignItems="flex-start">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>We couldn't load your credit applications</AlertTitle>
            <AlertDescription>Check your connection and try again.</AlertDescription>
          </Box>
          <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => refetch()}>
            Retry
          </Button>
        </Alert>
      );
    }

    if (filtered.length === 0) {
      return (
        <EmptyState
          icon={searchTerm ? FiSearch : FiInbox}
          title={searchTerm ? "No matching applications" : "No credit applications yet"}
          description={
            searchTerm
              ? `Nothing matches "${searchTerm}". Try another ID, status or date.`
              : "Request a credit account to get started."
          }
          action={
            searchTerm ? (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<FiRotateCcw />}
                onClick={() => {
                  setSearchTerm("");
                  // This button unmounts once rows reappear — hand focus back to the search field
                  searchRef.current?.focus();
                }}
              >
                Clear search
              </Button>
            ) : (
              newApplicationButton(createApplication.onOpen, "sm")
            )
          }
        />
      );
    }

    return (
      <>
        <DataTable label="Credit account applications">
          <Table>
            <Thead>
              <Tr>
                <Th scope="col">Application ID</Th>
                <Th scope="col">Status</Th>
                <Th scope="col">Creation Date</Th>
                <Th scope="col">Last Update</Th>
                <Th scope="col">
                  <VisuallyHidden>{ACTIONS_COLUMN}</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentItems.map((application) => {
                const open = () => {
                  setSelectedId(application.application_id);
                  viewApplication.onOpen();
                };
                return (
                  <Tr key={application.id} data-clickable="true" onClick={open} sx={clickableRowStyles}>
                    <Td fontWeight={600}>{application.application_id}</Td>
                    <Td>
                      <StatusBadge kind="application" value={application.status} />
                    </Td>
                    <Td fontWeight={600}>{formatShortDate(new Date(application.creation_date))}</Td>
                    <Td fontWeight={600}>{formatShortDate(new Date(application.last_update))}</Td>
                    <Td>
                      <RowActions onView={open} applicationId={application.application_id} />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </DataTable>
        <Pagination
          page={currentPage}
          pageSize={ITEMS_PER_PAGE}
          total={filtered.length}
          onChange={setPage}
          itemLabel="applications"
        />
      </>
    );
  };

  return (
    <SectionCard
      folio={folio}
      title="Credit account applications"
      lede="Your requests for a credit account"
      isLoading={isPending}
      toolbar={
        <Flex gap={3} wrap="wrap" w={{ base: "full", md: "auto" }}>
          <SearchField
            ref={searchRef}
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search applications"
            aria-label="Search credit applications"
          />
          <CreateApplicationModal
            kind="requestcredit"
            refetchApplications={refetch}
            disclosure={createApplication}
            renderTrigger={(onOpen) => newApplicationButton(onOpen)}
          />
        </Flex>
      }
    >
      <RequestCreditApplicationViewModal
        applicationId={selectedId}
        isOpen={viewApplication.isOpen}
        onClose={viewApplication.onClose}
      />
      {renderContent()}
    </SectionCard>
  );
};

export default CreditApplications;
